import { Component, Element, h, Host, Prop, State, Watch, Event, EventEmitter } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { BreadcrumbSchemeDefinition, BreadcrumbSchemeName, BreadcrumbSize } from "./constants/breadcrumb-types";
import {
  BREADCRUMB_SCHEME_CONFIG,
  BREADCRUMB_SCHEME_MAP,
  BREADCRUMPSIZE_SMALL
} from "./constants/breadcrumb-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { BreadcrumbSchemeConfiguration } from "../../constants/colors";
import { detectMarkupChanges } from "../../utils/slot-helper";
import Fragment from "../../common/fragment";

/**
 * @slot [unnamed] - Holds any number of default HTML anchor tags
 */
@Component({
  tag: "eon-ui-breadcrumb",
  styleUrl: "breadcrumb.scss",
  shadow: true
})
export class Breadcrumb implements ColorableComponent<BreadcrumbSchemeConfiguration<BreadcrumbSchemeDefinition>> {
  schemeConfig: BreadcrumbSchemeConfiguration<BreadcrumbSchemeDefinition> = BREADCRUMB_SCHEME_CONFIG;
  schemeMap = BREADCRUMB_SCHEME_MAP;

  @State() breadcrumbItems: Element[] = [];

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: BreadcrumbSchemeDefinition;

  @Element() hostElement: HTMLEonUiBreadcrumbElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: BreadcrumbSchemeName;

  /**
   * The size of the breadcrumb.
   */
  @Prop({ reflect: true }) size: BreadcrumbSize = BREADCRUMPSIZE_SMALL;

  /**
   * Raised when the component has been loaded.
   */
  @Event() breadcrumbLoad: EventEmitter;

  /**
   * Raised when the component has been loaded.
   */
  @Event() breadcrumbClick: EventEmitter<any>;

  componentWillLoad() {
    this.applyScheme();
    this.getBreadcrumbItems();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.breadcrumbLoad.emit();

    detectMarkupChanges(() => {
      this.getBreadcrumbItems();
    }, this.hostElement);
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  private getBreadcrumbItems() {
    this.breadcrumbItems.forEach((item) => item.removeAttribute("aria-current"));
    this.breadcrumbItems = Array.from(this.hostElement.children);
  }

  private emitClickEvent(e) {
    const detail = {
      target: e.target.href,
      title: e.target.innerText
    };

    this.breadcrumbClick.emit(detail);
  }

  render() {
    return (
      <Host>
        <nav class={pfx({ breadcrumb: true, [this.size]: true })} aria-label={"breadcrumb"}>
          <ol>
            {this.breadcrumbItems.map((breadcrumbItem: HTMLAnchorElement, index) => (
              <li>
                {index === this.breadcrumbItems.length - 1 ? (
                  <Fragment>
                    <a href={breadcrumbItem.href} aria-current="page">
                      {breadcrumbItem.innerText}
                    </a>
                  </Fragment>
                ) : (
                  <Fragment>
                    <a href={breadcrumbItem.href} onClick={(e) => this.emitClickEvent(e)}>
                      {breadcrumbItem.innerText}
                    </a>
                    <eon-ui-icon
                      name="chevron_small_right"
                      size={this.size === "small" ? "small" : "normal"}
                      no-space="false"
                    ></eon-ui-icon>
                  </Fragment>
                )}
              </li>
            ))}
          </ol>
        </nav>
      </Host>
    );
  }
}
